import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── Auth helpers ─────────────────────────────────────────────────────────────

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { data, error };
}

export async function signOut() {
  return await supabase.auth.signOut();
}

export async function getSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

// ── Profile helpers ──────────────────────────────────────────────────────────

export async function getProfile(userId) {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*, teams(name)')
    .eq('id', userId)
    .single();
  return { data, error };
}

// ── Project helpers ───────────────────────────────────────────────────────

export async function getProjects(teamId, includeArchived = false) {
  let query = supabase
    .from('engagements')
    .select('*')
    .eq('team_id', teamId)
    .order('updated_at', { ascending: false });
  if (!includeArchived) {
    query = query.eq('archived', false);
  }
  const { data, error } = await query;
  return { data, error };
}

export async function createProject(teamId, userId, fields) {
  const { data, error } = await supabase
    .from('engagements')
    .insert({ team_id: teamId, created_by: userId, ...fields })
    .select()
    .single();
  return { data, error };
}

export async function updateProject(id, fields) {
  const { data, error } = await supabase
    .from('engagements')
    .update(fields)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function archiveProject(id, archived) {
  const { data, error } = await supabase
    .from('engagements')
    .update({ archived })
    .eq('id', id)
    .select()
    .single();
  return { data, error };
}

export async function getProject(id) {
  const { data, error } = await supabase
    .from('engagements')
    .select('*')
    .eq('id', id)
    .single();
  return { data, error };
}

// ── Population helpers ───────────────────────────────────────────────────────

export async function savePopulation(engagementId, teamId, projects) {
  // Upsert — one population per engagement
  const { data, error } = await supabase
    .from('populations')
    .upsert({ engagement_id: engagementId, team_id: teamId, projects },
             { onConflict: 'engagement_id' })
    .select()
    .maybeSingle();
  return { data, error };
}

export async function getPopulation(engagementId) {
  const { data, error } = await supabase
    .from('populations')
    .select('*')
    .eq('engagement_id', engagementId)
    .maybeSingle();
  return { data, error };
}

// ── Sample helpers ───────────────────────────────────────────────────────────

export async function saveSample(engagementId, teamId, userId, sampleData) {
  const { data, error } = await supabase
    .from('samples')
    .upsert({ engagement_id: engagementId, team_id: teamId, drawn_by: userId, ...sampleData },
             { onConflict: 'engagement_id' })
    .select()
    .maybeSingle();
  return { data, error };
}

export async function getSample(engagementId) {
  const { data, error } = await supabase
    .from('samples')
    .select('*')
    .eq('engagement_id', engagementId)
    .maybeSingle();
  // Map snake_case DB fields to camelCase for ResultsPanel
  if (data) {
    data.samplingMode = data.sampling_mode || 'variable';
    data.attrPlan = data.attr_plan || null;
  }
  return { data, error };
}

// ── Score helpers ────────────────────────────────────────────────────────────

export async function saveScore(engagementId, teamId, userId, projectId, qualifiedPct) {
  const { data, error } = await supabase
    .from('scores')
    .upsert({
      engagement_id: engagementId,
      team_id: teamId,
      scored_by: userId,
      project_id: String(projectId),
      qualified_pct: qualifiedPct,
    }, { onConflict: 'engagement_id,project_id' })
    .select()
    .maybeSingle();
  return { data, error };
}

export async function getScores(engagementId) {
  const { data, error } = await supabase
    .from('scores')
    .select('*')
    .eq('engagement_id', engagementId);
  return { data, error };
}

// ── Report helpers ───────────────────────────────────────────────────────────

export async function saveReport(engagementId, teamId, userId, reportData) {
  const { data, error } = await supabase
    .from('reports')
    .upsert({ engagement_id: engagementId, team_id: teamId, generated_by: userId, data: reportData },
             { onConflict: 'engagement_id' })
    .select()
    .maybeSingle();
  return { data, error };
}

export async function getReport(engagementId) {
  const { data, error } = await supabase
    .from('reports')
    .select('*')
    .eq('engagement_id', engagementId)
    .maybeSingle();
  return { data, error };
}

// ── Credits helpers ──────────────────────────────────────────────────────────

export async function getCredits(teamId) {
  const { data, error } = await supabase
    .from('teams')
    .select('credits')
    .eq('id', teamId)
    .single();
  return { credits: data?.credits ?? 0, error };
}

export async function deductCredit(teamId) {
  // Uses RPC to atomically deduct 1 credit and return new balance
  const { data, error } = await supabase.rpc('deduct_credit', {
    p_team_id: teamId,
  });
  return { data, error };
}

// Single source of truth for Stripe product definitions.
// CreditsModal imports STRIPE_PRODUCTS to guarantee names stay in sync.
export const STRIPE_PRODUCTS = [
  { name: 'Single Credit',     credits: 1,   price: 500,   perCredit: 500, savings: null,      highlight: false, link: 'https://buy.stripe.com/aFa4gB4ZH69O7XNbTA9k400' },
  { name: 'Standard Bundle',   credits: 10,  price: 4250,  perCredit: 425, savings: '15% off', highlight: false, link: 'https://buy.stripe.com/14A3cx1Nv1Tyb9ZaPw9k401' },
  { name: 'Firm Bundle',       credits: 50,  price: 18500, perCredit: 370, savings: '26% off', highlight: true,  link: 'https://buy.stripe.com/9B6fZjdwdaq46TJaPw9k403' },
  { name: 'Enterprise Bundle', credits: 100, price: 33500, perCredit: 335, savings: '33% off', highlight: false, link: 'https://buy.stripe.com/dRm9AVbo5eGk3Hx3n49k404' },
];

export async function getStripeUrl(productName, teamId) {
  const product = STRIPE_PRODUCTS.find(p => p.name === productName);
  if (!product) return null;
  // Pass teamId|credits as client_reference_id — webhook uses this to credit the right team
  return `${product.link}?client_reference_id=${teamId}|${product.credits}`;
}

// ── Team management helpers ──────────────────────────────────────────────────

export async function getTeamMembers(teamId) {
  // Join user_profiles with auth.users to get email
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id, full_name, role, created_at, team_id')
    .eq('team_id', teamId)
    .order('created_at', { ascending: true });
  
  if (error) return { data: null, error };

  // Get emails from auth - we store them separately since auth.users isn't directly queryable
  // We'll use the user's own session for their email and admin API for others
  // For now return profiles — email will come from a separate RPC
  return { data, error };
}

export async function getTeamMembersWithEmail(teamId) {
  const { data, error } = await supabase
    .rpc('get_team_members', { p_team_id: teamId });
  return { data, error };
}

export async function inviteUser(email, password, fullName, teamId, role) {
  // Create auth user via admin signup (uses service role on backend)
  // Since we're client-side only, we use a Netlify function for this
  const response = await fetch('/.netlify/functions/create-user', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, fullName, teamId, role }),
  });
  const result = await response.json();
  if (!response.ok) return { error: { message: result.error || 'Failed to create user' } };
  return { data: result, error: null };
}

export async function updateMemberRole(userId, newRole) {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({ role: newRole })
    .eq('id', userId);
  return { data, error };
}

export async function removeMember(userId) {
  // Remove from team (don't delete auth user)
  const { data, error } = await supabase
    .from('user_profiles')
    .update({ team_id: null, role: 'member' })
    .eq('id', userId);
  return { data, error };
}

// ── Sample run log helpers ───────────────────────────────────────────────────

export async function logSampleRun(engagementId, teamId, userId, runData) {
  // Get next run number
  const { data: existing } = await supabase
    .from('sample_runs')
    .select('run_number')
    .eq('engagement_id', engagementId)
    .order('run_number', { ascending: false })
    .limit(1);

  const nextRun = existing && existing.length > 0 ? existing[0].run_number + 1 : 1;

  const { data, error } = await supabase
    .from('sample_runs')
    .insert({
      engagement_id: engagementId,
      team_id: teamId,
      drawn_by: userId,
      run_number: nextRun,
      ...runData,
    })
    .select()
    .single();
  return { data, error };
}

export async function getSampleRuns(engagementId) {
  const { data, error } = await supabase
    .from('sample_runs')
    .select('*')
    .eq('engagement_id', engagementId)
    .order('drawn_at', { ascending: false });
  return { data, error };
}

// ── Approval helpers ─────────────────────────────────────────────────────────

export async function submitForApproval(engagementId, planSettings) {
  const update = { approval_status: 'awaiting_approval' };
  if (planSettings) update.plan_settings = planSettings;
  const { data, error } = await supabase
    .from('engagements')
    .update(update)
    .eq('id', engagementId)
    .select().single();
  return { data, error };
}

export async function resetApproval(engagementId) {
  const { data, error } = await supabase
    .from('engagements')
    .update({ approval_status: 'pending' })
    .eq('id', engagementId)
    .select().single();
  return { data, error };
}

export async function approveEngagement(engagementId, userId, note = '') {
  const { data, error } = await supabase
    .from('engagements')
    .update({ approval_status: 'approved', approved_by: userId,
               approved_at: new Date().toISOString(), approval_note: note })
    .eq('id', engagementId)
    .select().single();
  return { data, error };
}

export async function rejectEngagement(engagementId, userId, note = '') {
  const { data, error } = await supabase
    .from('engagements')
    .update({ approval_status: 'rejected', approved_by: userId,
               approved_at: new Date().toISOString(), approval_note: note })
    .eq('id', engagementId)
    .select().single();
  return { data, error };
}

// ── Auditor helpers ───────────────────────────────────────────────────────────

export async function getAllTeams() {
  const { data, error } = await supabase
    .from('teams')
    .select('*')
    .order('name');
  return { data, error };
}

export async function getAllEngagements() {
  const { data, error } = await supabase
    .from('engagements')
    .select('*, teams(name)')
    .order('updated_at', { ascending: false })
    .limit(500);
  return { data, error };
}

export async function flagEngagement(engagementId, note) {
  const { data, error } = await supabase
    .from('engagements')
    .update({ flagged: true, flag_note: note })
    .eq('id', engagementId)
    .select().single();
  return { data, error };
}

export async function unflagEngagement(engagementId) {
  const { data, error } = await supabase
    .from('engagements')
    .update({ flagged: false, flag_note: null })
    .eq('id', engagementId)
    .select().single();
  return { data, error };
}

export async function suspendTeam(teamId, note) {
  const { data, error } = await supabase
    .from('teams')
    .update({ suspended: true, suspension_note: note })
    .eq('id', teamId)
    .select().single();
  return { data, error };
}

export async function unsuspendTeam(teamId) {
  const { data, error } = await supabase
    .from('teams')
    .update({ suspended: false, suspension_note: null })
    .eq('id', teamId)
    .select().single();
  return { data, error };
}

export async function getAllSampleRuns() {
  const { data, error } = await supabase
    .from('sample_runs')
    .select('*, engagements(client_name, team_id, teams(name))')
    .order('drawn_at', { ascending: false })
    .limit(200);
  return { data, error };
}

// ── EULA helpers ─────────────────────────────────────────────────────────────

export async function acceptEula(userId) {
  const { data, error } = await supabase
    .from('user_profiles')
    .update({ eula_accepted: true, eula_accepted_at: new Date().toISOString() })
    .eq('id', userId)
    .select().single();
  return { data, error };
}

export async function getTeamName(teamId) {
  const { data, error } = await supabase
    .from('teams')
    .select('name')
    .eq('id', teamId)
    .single();
  return { name: data?.name || '', error };
}
